// src/pages/inventory/settings/shopify/ShopifyCredentialsSection.tsx
import React, { useEffect, useState } from "react";
import { Input, InputNumber, Button, Typography, Alert, Space } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { createShopifyConfig, updateShopifyConfig } from "../../../../api/inventory/shopifyApi";
import type { ShopifyConfig, ShopifyCredentialsPayload } from "../../../../types/inventory/ShopifyTypes";
import { useNotifications } from "../../../../context/NotificationContext";
import { SectionCard } from "../../../../components/shared";

const { Text } = Typography;

interface ShopifyCredentialsSectionProps {
	config: ShopifyConfig | null;
	onSaved: (config: ShopifyConfig) => void;
}

interface FormState {
	shop_domain: string;
	api_key: string;
	api_secret: string;
	webhook_secret: string;
	oauth_scopes: string;
	rate_limit_delay: number;
	max_retries: number;
}

const buildFormState = (config: ShopifyConfig | null): FormState => ({
	shop_domain: config?.shop_domain || "",
	// Secrets are write-only — the API never returns them — so these always
	// start blank. "Leave blank to keep current value" applies whenever a
	// row already exists.
	api_key: "",
	api_secret: "",
	webhook_secret: "",
	oauth_scopes: config?.oauth_scopes || "",
	rate_limit_delay: config?.rate_limit_delay ?? 1,
	max_retries: config?.max_retries ?? 3,
});

// Staff-facing form for a clinic's Shopify app credentials — create or edit
// the ShopifyConfig row before (or after) a store is actually connected via
// OAuth. Follows IntegrationManagement.tsx's write-only secret pattern.
const ShopifyCredentialsSection: React.FC<ShopifyCredentialsSectionProps> = ({ config, onSaved }) => {
	const [formState, setFormState] = useState<FormState>(() => buildFormState(config));
	const [shopDomainError, setShopDomainError] = useState<string>("");
	const [saving, setSaving] = useState(false);
	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		setFormState(buildFormState(config));
		setShopDomainError("");
	}, [config]);

	const updateField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
		setFormState((prev) => ({ ...prev, [field]: value }));
		if (field === "shop_domain" && shopDomainError) {
			setShopDomainError("");
		}
	};

	const handleSave = async () => {
		if (!formState.shop_domain.trim()) {
			setShopDomainError("Shop domain is required");
			return;
		}

		const payload: ShopifyCredentialsPayload = {
			shop_domain: formState.shop_domain.trim(),
			oauth_scopes: formState.oauth_scopes || undefined,
			rate_limit_delay: formState.rate_limit_delay,
			max_retries: formState.max_retries,
		};

		// Only send secrets the staff actually retyped — blank means "keep
		// the current value" (enforced server-side too).
		if (formState.api_key) payload.api_key = formState.api_key;
		if (formState.api_secret) payload.api_secret = formState.api_secret;
		if (formState.webhook_secret) payload.webhook_secret = formState.webhook_secret;

		setSaving(true);
		try {
			const saved = config ? await updateShopifyConfig(config.id, payload) : await createShopifyConfig(payload);
			showSuccess(config ? "Shopify credentials updated" : "Shopify credentials saved");
			onSaved(saved);
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to save Shopify credentials");
		} finally {
			setSaving(false);
		}
	};

	return (
		<SectionCard
			title="Shopify App Credentials"
			subtitle="Configure your clinic's Shopify app before connecting a store"
			extra={
				<Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
					{config ? "Update Credentials" : "Save Credentials"}
				</Button>
			}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
				<Alert
					message="Credentials are encrypted at rest"
					description="API keys and secrets are stored encrypted and are never returned by the API. Leave a secret field blank to keep its current value."
					type="info"
					showIcon
				/>

				<div>
					<Text strong style={{ display: "block", marginBottom: 8 }}>
						Shop Domain <span style={{ color: "#ff4d4f" }}>*</span>
					</Text>
					<Input
						value={formState.shop_domain}
						onChange={(e) => updateField("shop_domain", e.target.value)}
						placeholder="my-store.myshopify.com"
						status={shopDomainError ? "error" : ""}
					/>
					{shopDomainError && (
						<Text type="danger" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
							{shopDomainError}
						</Text>
					)}
				</div>

				<div>
					<Text strong style={{ display: "block", marginBottom: 8 }}>
						API Key
					</Text>
					<Input.Password
						value={formState.api_key}
						onChange={(e) => updateField("api_key", e.target.value)}
						placeholder={config ? "Leave blank to keep current value" : "Your Shopify app API key"}
						autoComplete="new-password"
					/>
				</div>

				<div>
					<Text strong style={{ display: "block", marginBottom: 8 }}>
						API Secret
					</Text>
					<Input.Password
						value={formState.api_secret}
						onChange={(e) => updateField("api_secret", e.target.value)}
						placeholder={config ? "Leave blank to keep current value" : "Your Shopify app API secret"}
						autoComplete="new-password"
					/>
				</div>

				<div>
					<Text strong style={{ display: "block", marginBottom: 8 }}>
						Webhook Secret
					</Text>
					<Input.Password
						value={formState.webhook_secret}
						onChange={(e) => updateField("webhook_secret", e.target.value)}
						placeholder={config ? "Leave blank to keep current value" : "Shopify webhook signing secret"}
						autoComplete="new-password"
					/>
					<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
						Used to verify incoming Shopify webhook signatures. Enter the value from your Shopify app's webhook configuration — it is never generated for you.
					</Text>
				</div>

				<div>
					<Text strong style={{ display: "block", marginBottom: 8 }}>
						OAuth Scopes
					</Text>
					<Input
						value={formState.oauth_scopes}
						onChange={(e) => updateField("oauth_scopes", e.target.value)}
						placeholder="read_products,write_products,read_orders,write_orders"
					/>
				</div>

				<Space size={16}>
					<div>
						<Text strong style={{ display: "block", marginBottom: 8 }}>
							Rate Limit Delay (s)
						</Text>
						<InputNumber min={0} value={formState.rate_limit_delay} onChange={(value) => updateField("rate_limit_delay", value ?? 1)} />
					</div>
					<div>
						<Text strong style={{ display: "block", marginBottom: 8 }}>
							Max Retries
						</Text>
						<InputNumber min={0} value={formState.max_retries} onChange={(value) => updateField("max_retries", value ?? 3)} />
					</div>
				</Space>
			</div>
		</SectionCard>
	);
};

export default ShopifyCredentialsSection;
